import React, { useState, useEffect, useRef } from "react";
import {
  RatingOverviewSection,
  RatingOverviewContent,
  RatingScoreSection,
  RatingScore,
  StarContainer,
  TotalReviews,
  RatingBreakdownSection,
  RatingBreakdownItem,
  RatingNumber,
  RatingBar,
  RatingBarFill,
  RatingPercentage,
  ReviewsSection,
  ReviewsHeader,
  ReviewsTitle,
  FilterScrollContainer,
  FilterTabs,
  FilterTab,
  ReviewsList,
  ReviewItem,
  ReviewItemContent,
  ReviewHeader,
  ReviewerAvatar,
  ReviewerInfo,
  ReviewerName,
  ReviewDate,
  ReviewActions,
  ReviewMenuButton,
  ReviewMenuDropdown,
  ReviewMenuOption,
  ReviewStars,
  ReviewContent,
  LoadingContainer,
  LoadingSpinner,
  EmptyState,
  EmptyStateTitle,
  EmptyStateDescription,
  RatingLoadingContainer,
  RatingContainer,
  ReviewsHeaderLine,
  ModalOverlay,
  ModalContent,
  ModalTitle,
  ModalSubtitle,
  ReasonsList,
  ReasonRow,
  Checkbox,
  ButtonsRow,
  BackButton,
  CheckMark,
  ReasonText,
  ReportButton,
} from "../../styles/Rating.styled";
import { GET, POST } from "../../services/HTTPServices";
import { useErrorResponse } from "../../services/useErrorResponse";
import ResponsivePagination from "../../components/pagination/ResponsivePagination";
import { Box } from "@mui/material";
import { ALERT_TITLES, defaultPageSize } from "../../constants";
import { useMediaQuery } from "@mui/material";
import {
  alertMessage,
  alertMessageWithCancel,
} from "../../services/alertService";

// Import SVG components
import { ReactComponent as FullStar } from "../../assets/svgs/Rating/FullStar.svg";
import { ReactComponent as EmptyStar } from "../../assets/svgs/Rating/EmptyStar.svg";
import { DashboardContainer } from "../../styles/dashboard";
import { useSidebar } from "../../context/SidebarContext";


// Types
interface RatingOverviewData {
  serviceProviderId: number;
  serviceProviderName: string;
  rating: number;
  totalRating: number;
  star5: number;
  star4: number;
  star3: number;
  star2: number;
  star1: number;
}

interface Review {
  id: string;
  serviceProviderId: number;
  serviceProviderName: string;
  ratedAt: string;
  ratedBy: number;
  ratedByName: string;
  feedback: string;
  rating: number;
}

type FilterType = "all" | "5" | "4" | "3" | "2" | "1";

const Rating: React.FC = () => {
  const [ratingData, setRatingData] = useState<RatingOverviewData | null>(null);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [selectedReview, setSelectedReview] = useState<Review | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState<FilterType>("all");
  const [openMenuId, setOpenMenuId] = useState<any | null>(null);
  // Create refs for each menu button
  const menuRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const [pageNo, setPageNo] = React.useState(1);
  const [auth, setAuth] = React.useState<any>(null);
  const [totalPageCount, setTotalPageCount] = React.useState(0);
  const [showReportModal, setShowReportModal] = useState(false);
  const REPORT_REASONS = [
    "Off Topic",
    "Spam",
    "Conflict of Interest",
    "Profanity",
    "Bullying/Harassment",
    "Discrimination or hate speech",
    "Personal Information",
    "Not helpful",
  ];
  const [selectedReasons, setSelectedReasons] = useState<string[]>([]);

  const outErrorResponse = useErrorResponse();
  const { isSidebarCollapsed } = useSidebar();

  const isMobile = useMediaQuery("(max-width:767px)");

  // API functions defined before useEffect
  const getOverview = async (id: number) => {
    let url = "serviceprovider/GetServiceProviderTotalRating?spId=" + id;

    let res: any = await GET(url, "provider");
    if (res && res.status === 200) {
      setRatingData(res.data);
    } else {
      outErrorResponse(res.status, res.message);
    }
  };

  const getReviews = async (id: number, rating: string, page: any = pageNo) => {
    let url =
      "serviceprovider/GetServiceProviderRatingBySP?spId=" +
      id +
      (rating === "all" ? "" : "&rating=" + rating) +
      "&pageNo=" +
      page +
      "&pageSize=" +
      defaultPageSize;

    let res: any = await GET(url, "provider");
    if (res && res.status === 200) {
      setReviews(res.data.data);
      setTotalPageCount(res.data.totalPages);
      setLoading(false);
    } else {
      outErrorResponse(res.status, res.message);
    }
  };

  const pageClicked = async (page: any) => {
    setPageNo(page);
    getReviews(auth.id, activeFilter, page);
  };

  // Fetch rating overview data
  useEffect(() => {
    const authUser = JSON.parse(localStorage.getItem("authUser") || "null");
    if (authUser) {
      setAuth(authUser);
      getOverview(authUser.id);
      getReviews(authUser.id, activeFilter, pageNo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle click outside to close menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      // Don't close if clicking on dropdown menu or its options
      if (target.closest("[data-dropdown-menu]")) {
        return;
      }

      // Check if click is outside all menu containers
      const isOutside = Object.values(menuRefs.current).every(
        (ref) => !ref || !ref.contains(target)
      );

      if (isOutside) {
        setOpenMenuId(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleFilterChange = (filter: FilterType) => {
    setActiveFilter(filter);
    getReviews(auth.id, filter, pageNo);
  };


  const handleReportReview = async (
    review: Review,
    event: React.MouseEvent
  ) => {
    event.preventDefault();
    event.stopPropagation();
    setOpenMenuId(null);
    setSelectedReview(review);
    setSelectedReasons([]);
    setShowReportModal(true);
  };

  // Handle click outside to close menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      // Don't close if clicking on dropdown menu or its options
      if (target.closest("[data-dropdown-menu]")) {
        return;
      }

      if (menuRef.current && !menuRef.current.contains(target)) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const clickedReportReview = async () => {
    // Require at least one reason
    if (!selectedReasons || selectedReasons.length === 0) {
      alertMessage(ALERT_TITLES.ERROR, "Please select at least one reason.");
      return;
    }
    const confirmLeave = await alertMessageWithCancel(
      ALERT_TITLES.INFO,
      "Are you sure to report this review?"
    );
    if (confirmLeave) {
      reportReview();
    }
  };

  const reportReview = async (review: any = selectedReview) => {
    let url = "reprotrating/CreateReportRating";

    let param = {
      ratingId: review?.id,
      reportDate: new Date().toISOString(),
      serviceProviderId: review?.serviceProviderId,
      description: review?.feedback,
      reasons: selectedReasons,
    };

    let res: any = await POST(url, param, "provider");
    if (res && res.status === 200) {
      alertMessage(ALERT_TITLES.SUCCESS, "Review Reported.");
      setLoading(false);
      setShowReportModal(false);
    } else {
      setLoading(false);
      outErrorResponse(res.status, res.message);
    }
  };

  const renderStars = (
    rating: number,
    size: "small" | "large" | "medium" = "small"
  ) => {
    const stars = [];
    const starSize = size === "small" ? 12 : size === "large" ? 24 : 18;

    for (let i = 1; i <= 5; i++) {
      stars.push(
        i <= rating ? (
          <FullStar key={i} width={starSize} height={starSize} />
        ) : (
          <EmptyStar key={i} width={starSize} height={starSize} />
        )
      );
    }
    return stars;
  };

  const toggleReason = (reason: string) => {
    setSelectedReasons((prev) =>
      prev.includes(reason)
        ? prev.filter((r) => r !== reason)
        : [...prev, reason]
    );
  };

  if (loading) {
    return (
      <RatingLoadingContainer>
        <LoadingContainer>
          <LoadingSpinner />
        </LoadingContainer>
      </RatingLoadingContainer>
    );
  }

  if (!ratingData) {
    return (
      <RatingContainer>
        <EmptyState>
          <EmptyStateTitle>Unable to load rating data</EmptyStateTitle>
          <EmptyStateDescription>Please try again later</EmptyStateDescription>
        </EmptyState>
      </RatingContainer>
    );
  }

  const handleMenuToggle = (reviewId: string) => {
    setOpenMenuId(reviewId);
  };

  return (
    <DashboardContainer $isSidebarCollapsed={isSidebarCollapsed}>
      <RatingOverviewSection>
        <RatingOverviewContent>
          <RatingScoreSection>
            <RatingScore>{ratingData.rating.toFixed(1)}</RatingScore>
            <StarContainer>
              {renderStars(ratingData.rating, isMobile ? "small" : "large")}
            </StarContainer>
            <TotalReviews>{ratingData.totalRating} reviews</TotalReviews>
          </RatingScoreSection>

          <RatingBreakdownSection>
            {[5, 4, 3, 2, 1].map((star) => {
              const percentage = ratingData[
                `star${star}` as keyof RatingOverviewData
              ] as number;
              return (
                <RatingBreakdownItem key={star}>
                  <RatingNumber>{star}</RatingNumber>
                  <RatingBar>
                    <RatingBarFill $percentage={percentage} />
                  </RatingBar>
                  <RatingPercentage>{percentage}%</RatingPercentage>
                </RatingBreakdownItem>
              );
            })}
          </RatingBreakdownSection>
        </RatingOverviewContent>
      </RatingOverviewSection>

      {/* Reviews Section */}
      <ReviewsSection>
        <ReviewsHeader className="mb-16">
          <ReviewsTitle>All Reviews</ReviewsTitle>
        </ReviewsHeader>

        <ReviewsHeaderLine className="mb-16" />

        <FilterScrollContainer>
          <FilterTabs className="mb-32">
            <FilterTab
              $active={activeFilter === "all"}
              onClick={() => handleFilterChange("all")}
            >
              All
            </FilterTab>
            {[5, 4, 3, 2, 1].map((star) => (
              <FilterTab
                key={star}
                $active={activeFilter === star.toString()}
                onClick={() =>
                  handleFilterChange(star.toString() as FilterType)
                }
              >
                {star} star
              </FilterTab>
            ))}
          </FilterTabs>
        </FilterScrollContainer>

        <ReviewsList>
          {reviews.length === 0 ? (
            <EmptyState>
              <EmptyStateTitle>No reviews here.</EmptyStateTitle>
            </EmptyState>
          ) : (
            reviews.map((review: any) => (
              <ReviewItem key={review.id}>
                <ReviewItemContent>
                  <ReviewHeader>
                    <ReviewerAvatar className="mr-16">
                      {review.ratedByName?.charAt(0).toUpperCase()}
                    </ReviewerAvatar>
                    <ReviewerInfo>
                      <ReviewerName>{review.ratedByName}</ReviewerName>
                      <ReviewDate>{review.ratedAt}</ReviewDate>
                    </ReviewerInfo>
                    {/* <ReviewActionsComponent
                      reviewId={review.id}
                      review={review}
                      isOpen={openMenuId === review.id}
                      onMenuClick={handleMenuButtonClick}
                      onReportClick={handleReportReview}
                      menuRef={(el) => { menuRefs.current[review.id] = el; }}
                    /> */}

                    <ReviewActions ref={menuRef}>
                      <ReviewMenuButton
                        onClick={() => handleMenuToggle(review.id)}
                      >
                        ⋮
                      </ReviewMenuButton>

                      <ReviewMenuDropdown
                        data-dropdown-menu
                        // eslint-disable-next-line eqeqeq
                        $isOpen={openMenuId == review.id}
                        style={{ zIndex: 2100 }}
                      >
                        <ReviewMenuOption
                          onClick={(event) => handleReportReview(review, event)}
                        >
                          Report
                        </ReviewMenuOption>
                      </ReviewMenuDropdown>
                    </ReviewActions>
                  </ReviewHeader>
                  <ReviewStars>
                    {renderStars(review.rating, isMobile ? "small" : "large")}
                  </ReviewStars>
                  <ReviewContent>{review.feedback}</ReviewContent>
                </ReviewItemContent>
              </ReviewItem>
            ))
          )}
        </ReviewsList>

        <Box mt={4} display="flex" justifyContent="center">
          <ResponsivePagination
            currentPage={pageNo}
            totalPageCount={totalPageCount}
            pageSize={defaultPageSize}
            onPageChange={(page: any) => pageClicked(page)}
          />
        </Box>
      </ReviewsSection>

      {showReportModal && (
        <ModalOverlay onClick={() => setShowReportModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>Report Review</ModalTitle>
            <ModalSubtitle>(You may select more than one reason)</ModalSubtitle>

            <ReasonsList>
              {REPORT_REASONS.map((reason) => (
                <ReasonRow key={reason} onClick={() => toggleReason(reason)}>
                  <Checkbox $checked={selectedReasons.includes(reason)}>
                    {selectedReasons.includes(reason) && <CheckMark />}
                  </Checkbox>
                  <ReasonText>{reason}</ReasonText>
                  {/* {selectedReasons.includes(reason) && (
                    <MarkedLabel>Marked</MarkedLabel>
                  )} */}
                </ReasonRow>
              ))}
            </ReasonsList>

            <ButtonsRow>
              <BackButton
                type="button"
                onClick={() => setShowReportModal(false)}
              >
                Back
              </BackButton>
              <ReportButton type="button" onClick={clickedReportReview}>
                Report Review
              </ReportButton>
            </ButtonsRow>
          </ModalContent>
        </ModalOverlay>
      )}
    </DashboardContainer>
  );
};

export default Rating;
